import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { berlinDate, type Shift } from '@kontor/shared';
import { api, queryString } from '../../api';
import { LeaveRequestForm, useMyEmployee } from '../hr';
import { hoursDe, movePeriod, periodOf, SHIFT_VIEW_LABELS, type ShiftView } from './format';
import { ShiftBoard } from './ShiftBoard';
import { ShiftDayGrid } from './ShiftDayGrid';
import { GridStepSelect, useGridStep } from './gridStep';
import './shifts.css';

/**
 * „Mein Konto“ → Mein Schichtplan (KON-040): die veröffentlichten Schichten des eigenen Mitarbeiters als Kalenderraster, darunter der Schnellantrag auf
 * Freistellung. Der Server liefert hier nie Schichten anderer Mitarbeiter.
 */
export function MyShifts() {
  const [view, setView] = useState<ShiftView>('week');
  const [step, setStep] = useGridStep();
  const [anchor, setAnchor] = useState(berlinDate());
  const period = periodOf(view, anchor);
  const mine = useMyEmployee();
  const shifts = useQuery({
    queryKey: ['my-shifts', period.from, period.to],
    queryFn: async () => (await api<{ data: Shift[] }>('GET', `/shifts/mine${queryString({ from: period.from, to: period.to })}`)).data,
  });
  const list = shifts.data ?? [];
  const total = list.reduce((sum, s) => sum + s.hours, 0);

  return (
    <div className="me-stack">
      <section className="card">
        <h2>Mein Schichtplan</h2>
        <div className="shift-toolbar">
          <div className="shift-toolbar__nav" role="group" aria-label="Zeitraum">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(movePeriod(view, anchor, -1))} aria-label="Zurück">←</button>
            <strong className="shift-toolbar__label">{period.label}</strong>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(movePeriod(view, anchor, 1))} aria-label="Weiter">→</button>
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAnchor(berlinDate())}>Heute</button>
          </div>
          <label className="field shift-toolbar__field">
            <span>Ansicht</span>
            <select className="input" value={view} onChange={(e) => setView(e.target.value as ShiftView)}>
              {(['day', 'week', 'month'] as ShiftView[]).map((v) => <option key={v} value={v}>{SHIFT_VIEW_LABELS[v]}</option>)}
            </select>
          </label>
          {view === 'day' && <GridStepSelect value={step} onChange={setStep} />}
        </div>
        {shifts.isError && <div className="alert alert--error" role="alert">{(shifts.error as Error).message}</div>}
        <p className="muted small">{list.length > 0 ? `${list.length} Schichten, ${hoursDe(total)} im Zeitraum.` : 'Keine veröffentlichten Schichten im Zeitraum.'} Entwürfe des Büros sehen Sie erst nach der Veröffentlichung.</p>
        {shifts.isLoading ? <p className="muted">Wird geladen …</p> : view === 'day' ? <ShiftDayGrid day={anchor} step={step} shifts={list} /> : <ShiftBoard days={period.days} month={view === 'month'} shifts={list} showNames={false} />}
      </section>

      {mine.data ? (
        <LeaveRequestForm employee={mine.data} />
      ) : (
        !mine.isLoading && (
          <section className="card">
            <h2>Freistellung beantragen</h2>
            <p className="muted">Ihr Zugang ist mit keinem Mitarbeiter verknüpft. Wenden Sie sich an das Büro.</p>
          </section>
        )
      )}
    </div>
  );
}
